// The eight tube reload looks of mockup round 064 (R1474,
// `ui-map-interface.md` §6.3): card 06 CLOCK DIGIT, the AUTO look, card 03
// TUBE GAUGE, card 10 SWEEP SEGMENTS, card 07 LOADING SLIDE, card 04
// COUNTDOWN TAG, card 05 ROUND LABEL, card 02 PIP ROW and card 01 ARC BAR.
//
// One entry per look id of `labels.toml reload_styles`. `RECIPES` holds the
// built cards and `PENDING` the ones still to build; a pending look draws
// the AUTO look, and `reload-styles.test.ts` pins that the two lists cover
// the whole list once.
//
// Every FIGURE is in the look's `fig` table of
// `data/presentation/reload.toml`, and a recipe reads it by name through
// `figs`, so a name the row does not carry is a type error.

import {
  TAU, arc, clamp01, dashedArc, dashedLine, disc, easeQuad, flash, frac,
  line, rect, rectStroke, ring, sector,
  type ReloadCtx, type ReloadRecipe, type ReloadShape,
} from './reload-recipe-kit';
import { RELOAD_LIMITS, RELOAD_LOOKS } from '../gen/presentation';
import { chart as S } from '../gen/strings';

const INK = RELOAD_LIMITS.ink;
const FLASH = RELOAD_LIMITS.flash;
const FAINT = RELOAD_LIMITS.faint;
const DEEP = RELOAD_LIMITS.deep;
const GROUND = RELOAD_LIMITS.ground;
const TOP = -Math.PI / 2;

/** The figures of one look, typed by the row the generator wrote. */
type Figs<K extends keyof typeof RELOAD_LOOKS> = (typeof RELOAD_LOOKS)[K]['fig'];
function figs<K extends keyof typeof RELOAD_LOOKS>(c: ReloadCtx, _id: K): Figs<K> {
  return c.row.fig as Figs<K>;
}

// ---------------------------------------------------------------------------
// The words. Each one is built once and kept, so a frame builds no string
// (§10.4): a countdown moves once a second, and a label once a round.
// ---------------------------------------------------------------------------

const tagWords = new Map<number, string>();
/** `RLD 14`: the tag of card 04 while the tube reloads. */
function tagReloading(sec: number): string {
  let s = tagWords.get(sec);
  if (s === undefined) { s = S.reload_tag_reloading({ sec }); tagWords.set(sec, s); }
  return s;
}

const digitWords = new Map<number, string>();
/** The whole seconds in the face of card 06, built once a figure (§10.4).
 *  The guard pip prints the same digit (R2418). */
export function clockDigits(sec: number): string {
  let s = digitWords.get(sec);
  if (s === undefined) { s = String(sec); digitWords.set(sec, s); }
  return s;
}

const labelWords = new Map<string, Map<number, string>>();
/** `7 REMORA · 14s` or `3 HARPOON`: the label of card 05. `sec < 0` drops
 *  the seconds. The key packs the count and the seconds into one number. */
function label(n: number, name: string, sec: number): string {
  let byName = labelWords.get(name);
  if (byName === undefined) { byName = new Map(); labelWords.set(name, byName); }
  const key = sec * 1024 + n;
  let s = byName.get(key);
  if (s === undefined) {
    s = sec < 0 ? S.reload_label_ready({ n, round: name })
      : S.reload_label_reloading({ n, round: name, sec });
    byName.set(key, s);
  }
  return s;
}

// ---------------------------------------------------------------------------
// Card 02, PIP ROW
// ---------------------------------------------------------------------------

/**
 * How many strips fill a diamond or a disc part of the way. The card clips
 * a rectangle to the pip; the stroke batch has no clip, so a part fill is
 * this many horizontal strips, each as wide as the shape at its middle. A
 * full pip is the exact shape.
 */
const PIP_STRIPS = 5;

/** One pip at (px, py): `fill` 0 to 1 fills it from the bottom. It is
 *  `pip` of the page. `shape` is the kind of the round in that place
 *  (R2031); it is the firing kind's shape by default. */
function pip(
  c: ReloadCtx, px: number, py: number, fill: number, ink: number, a: number, rim = ink,
  shape: ReloadShape = c.shape,
): void {
  const F = figs(c, 'pip-row');
  const r = F.r_px;
  const h = shape === 'square' ? r : shape === 'diamond' ? r * F.diamond_ratio : r * F.disc_ratio;
  if (fill > 0) {
    const top = py + r * F.fill_ratio - 2 * F.fill_ratio * r * fill;
    const y0 = Math.max(top, py - h), y1 = py + h;
    if (y1 > y0) {
      if (shape === 'square') {
        rect(c, px - r, y0, 2 * r, y1 - y0, ink, a);
      } else if (y0 <= py - h) {
        if (shape === 'disc') disc(c.ground, px, py, h, ink, a);
        // A diamond is a square turned 45 degrees: one butt stroke as wide
        // as its side, along its diagonal.
        else c.segs.line(px - h / 2, py - h / 2, px + h / 2, py + h / 2, h * Math.SQRT2, ink, a, 0);
      } else {
        const step = (y1 - y0) / PIP_STRIPS;
        for (let k = 0; k < PIP_STRIPS; k++) {
          const ym = y0 + (k + 0.5) * step, d = ym - py;
          const half = shape === 'diamond' ? h - Math.abs(d) : Math.sqrt(Math.max(0, h * h - d * d));
          rect(c, px - half, ym - step / 2, 2 * half, step, ink, a);
        }
      }
    }
  }
  const ra = fill >= 1 ? a : Math.max(F.rim_floor_alpha, a * F.rim_share);
  if (shape === 'square') {
    rectStroke(c, px - r, py - r, 2 * r, 2 * r, F.rim_width_px, rim, ra);
  } else if (shape === 'diamond') {
    line(c, px, py - h, px + h, py, F.rim_width_px, rim, ra);
    line(c, px + h, py, px, py + h, F.rim_width_px, rim, ra);
    line(c, px, py + h, px - h, py, F.rim_width_px, rim, ra);
    line(c, px - h, py, px, py - h, F.rim_width_px, rim, ra);
  } else {
    ring(c, c.rings, px, py, h, F.rim_width_px, rim, ra);
  }
}

/**
 * One pip per round of the magazine under the hull, in one row, or two
 * rows when the magazine is larger than `row_count`. The first pip is the
 * tube: it fills from the bottom while the tube reloads and rings once in
 * white at ready. The lit pips are the rounds aboard and a hollow pip is an
 * empty place. Empty tubes show every pip hollow in the faint ink.
 */
function pipRow(c: ReloadCtx): void {
  const F = figs(c, 'pip-row');
  const mag = c.mag;
  const perRow = mag > F.row_count ? Math.ceil(mag / 2) : mag;
  const pitch = F.pitch_px;
  const y0 = c.y + F.y_px;
  for (let i = 0; i < mag; i++) {
    const col = i % perRow, row = Math.floor(i / perRow);
    const px = c.x + (col - (perRow - 1) / 2) * pitch, py = y0 + row * pitch;
    if (c.empty) { pip(c, px, py, 0, FAINT, F.empty_alpha); continue; }
    if (i === 0) {
      const f = c.reloading ? c.p : 1;
      const k = c.ready ? flash(c.readyAgo, F.flash_sec) : 0;
      pip(c, px, py, f, k > 0 ? FLASH : INK, F.tube_alpha, FLASH);
      if (k > 0) {
        ring(c, c.rings, px, py, F.r_px + F.ring_gap_px + F.ring_travel_px * (1 - k),
          F.ring_width_px, FLASH, F.ring_alpha * k);
      }
    } else if (i < c.count) {
      pip(c, px, py, 1, INK, F.lit_alpha, INK, c.shapes[i] ?? c.shape);
    } else {
      pip(c, px, py, 0, FAINT, F.hollow_alpha);
    }
  }
}

// ---------------------------------------------------------------------------
// Card 10, SWEEP SEGMENTS
// ---------------------------------------------------------------------------

/**
 * A ring of segments inside the bracket corners. Each segment is one step
 * of the reload: they light one by one, clockwise from the top, and the
 * newest lights white for a moment. At ready the ring clicks: every segment
 * flashes white and snaps out and back. Then the full ring stays at a low
 * alpha. Empty tubes show short dim stubs.
 */
function sweepSegments(c: ReloadCtx): void {
  const F = figs(c, 'sweep-segments');
  const n = F.segment_count, R = F.r_px, seg = TAU / n, span = seg - F.gap_rad;
  const lit = c.reloading ? Math.floor(c.p * n) : n;
  const k = c.ready ? flash(c.readyAgo, F.flash_sec) : 0;
  const s = c.rings;
  for (let i = 0; i < n; i++) {
    const a0 = TOP + i * seg + F.gap_rad / 2;
    if (c.empty) {
      const m = a0 + span / 2;
      arc(c, s, c.x, c.y, R, F.stub_width_px, m - F.stub_rad, 2 * F.stub_rad, FAINT, F.stub_alpha);
    } else if (c.ready) {
      arc(c, s, c.x, c.y, R + F.click_px * k, F.unlit_width_px + F.click_width_px * k,
        a0, span, k > 0 ? FLASH : INK, F.ready_alpha + F.click_alpha * k);
    } else if (i < lit) {
      const fresh = i === lit - 1 && frac(c.p * n) < F.fresh_share;
      arc(c, s, c.x, c.y, R, F.lit_width_px, a0, span,
        fresh ? FLASH : INK, fresh ? F.fresh_alpha : F.lit_alpha);
    } else {
      arc(c, s, c.x, c.y, R, F.unlit_width_px, a0, span, INK, F.unlit_alpha);
    }
  }
}

// ---------------------------------------------------------------------------
// Card 07, LOADING SLIDE
// ---------------------------------------------------------------------------

/** The cosine and the sine of the heading. `loadingSlide` sets them once a
 *  frame, before it reads a point of the hull frame. */
let headCos = 1, headSin = 0;

/** A point of the hull frame, turned by the heading into world px. Card 07
 *  draws in that frame: +x ahead, +y to starboard. */
function hullX(c: ReloadCtx, lx: number, ly: number): number {
  return c.x + lx * headCos - ly * headSin;
}
function hullY(c: ReloadCtx, lx: number, ly: number): number {
  return c.y + lx * headSin + ly * headCos;
}

/** The rim of a capsule in the hull frame, from `x0` to `x1` on the line
 *  `y`, of half width `r`: two half circles and two straight sides. It is
 *  `capsule` of the page, stroked. */
function capsuleRim(
  c: ReloadCtx, x0: number, x1: number, y: number, r: number,
  w: number, ink: number, a: number, dash: number,
): void {
  const h = c.head;
  const lx = x0 + r, rx = x1 - r;
  const ax = hullX(c, lx, y), ay = hullY(c, lx, y);
  const bx = hullX(c, rx, y), by = hullY(c, rx, y);
  // The normal of the axis, to starboard.
  const nx = -headSin * r, ny = headCos * r;
  if (dash > 0) {
    dashedArc(c, c.rings, ax, ay, r, w, h + Math.PI / 2, Math.PI, dash, dash, ink, a);
    dashedArc(c, c.rings, bx, by, r, w, h - Math.PI / 2, Math.PI, dash, dash, ink, a);
    dashedLine(c, ax - nx, ay - ny, bx - nx, by - ny, w, dash, dash, ink, a);
    dashedLine(c, bx + nx, by + ny, ax + nx, ay + ny, w, dash, dash, ink, a);
    return;
  }
  arc(c, c.rings, ax, ay, r, w, h + Math.PI / 2, Math.PI, ink, a, false);
  arc(c, c.rings, bx, by, r, w, h - Math.PI / 2, Math.PI, ink, a, false);
  line(c, ax - nx, ay - ny, bx - nx, by - ny, w, ink, a, false);
  line(c, bx + nx, by + ny, ax + nx, ay + ny, w, ink, a, false);
}

/** A stroke between two points of the hull frame. */
function hullLine(
  c: ReloadCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, ink: number, a: number,
): void {
  line(c, hullX(c, x0, y0), hullY(c, x0, y0), hullX(c, x1, y1), hullY(c, x1, y1), w, ink, a);
}

/**
 * A tube along the starboard flank, in the hull frame, so it turns with the
 * heading. While the tube reloads, a round slides from astern into it, slow
 * at the start and slow at the end, and fades in over the first part of
 * the slide. At ready the muzzle doors close with a white flash; after a
 * shot they stand open and a small ring leaves the muzzle. The round
 * carries its kind: a plain round is a Harpoon, a ring at the nose is a
 * Remora, a band is a Torpor. Empty tubes show a dashed tube with open
 * doors and no round.
 */
function loadingSlide(c: ReloadCtx): void {
  const F = figs(c, 'loading-slide');
  const ty = F.tube_y_px, tx0 = F.tube_x_px, tx1 = F.tube_x_px + F.tube_length_px;
  headCos = Math.cos(c.head); headSin = Math.sin(c.head);
  capsuleRim(c, tx0, tx1, ty, F.tube_r_px, F.tube_width_px,
    c.empty ? FAINT : INK, c.empty ? F.empty_alpha : F.tube_alpha, c.empty ? F.dash_px : 0);
  if (!c.empty) {
    const xr = c.reloading ? F.round_start_px + F.slide_px * easeQuad(c.p) : F.round_start_px + F.slide_px;
    const a = c.reloading ? clamp01(c.p * F.fade_in_ratio) : 1;
    const rr = F.round_r_px, len = F.round_length_px;
    // A capsule filled is one stroke with round caps, as wide as the round.
    const x0 = xr + rr, x1 = xr + len - rr;
    c.segs.line(hullX(c, x0, ty), hullY(c, x0, ty), hullX(c, x1, ty), hullY(c, x1, ty),
      2 * rr, c.reloading ? INK : FLASH, F.round_alpha * a, c.round);
    if (c.shape === 'diamond') {
      const nx = xr + len + F.nose_px;
      ring(c, c.rings, hullX(c, nx, ty), hullY(c, nx, ty), F.nose_r_px, F.nose_width_px, FLASH, a);
    }
    if (c.shape === 'disc') {
      const bx = xr + len / 2;
      hullLine(c, bx, ty - F.band_px, bx, ty + F.band_px, F.band_width_px, FLASH, a);
    }
  }
  if (c.ready) {
    const k = flash(c.readyAgo, F.flash_sec);
    const dx = tx1 + F.door_gap_px;
    hullLine(c, dx, ty - F.door_px, dx, ty + F.door_px, F.door_width_px + F.door_flash_px * k,
      k > 0 ? FLASH : INK, F.door_alpha + F.door_flash_alpha * k);
  } else {
    const ink = c.empty ? FAINT : INK;
    hullLine(c, tx1, ty - F.tube_r_px, tx1 + F.open_dx_px, ty - F.open_dy_px, F.open_width_px, ink, F.door_alpha);
    hullLine(c, tx1, ty + F.tube_r_px, tx1 + F.open_dx_px, ty + F.open_dy_px, F.open_width_px, ink, F.door_alpha);
  }
  if (!c.empty && c.firedAgo >= 0 && c.firedAgo < F.fired_sec) {
    const q = c.firedAgo / F.fired_sec;
    const mx = tx1 + F.muzzle_px + F.muzzle_travel_px * q;
    ring(c, c.rings, hullX(c, mx, ty), hullY(c, mx, ty),
      F.muzzle_r_px + F.muzzle_grow_px * q, F.muzzle_width_px, FLASH, F.muzzle_alpha * (1 - q));
  }
}

// ---------------------------------------------------------------------------
// Card 06, CLOCK DIGIT (the AUTO look)
// ---------------------------------------------------------------------------

/**
 * A small clock face at the upper right of the hull, as big as the guard pip. A short hand on the
 * rim sweeps one lap over the reload and leaves a pale sector behind it,
 * and the whole seconds left stand in the face. At ready the face fills,
 * the rim turns white and a tick replaces the number. Empty tubes show a
 * hollow dashed face with a bar in it and no hand.
 */
function clockDigit(c: ReloadCtx): void {
  const F = figs(c, 'clock-digit');
  const cx = c.x + F.x_px, cy = c.y + F.y_px, R = F.r_px;
  disc(c.ground, cx, cy, R, DEEP, F.face_alpha);
  if (c.empty) {
    dashedArc(c, c.rings, cx, cy, R, F.empty_width_px, TOP, TAU, F.empty_dash_px,
      F.empty_dash_px, FAINT, F.empty_alpha);
    line(c, cx - F.bar_px, cy, cx + F.bar_px, cy, F.bar_width_px, FAINT, F.bar_alpha);
    return;
  }
  if (c.reloading) {
    const a1 = TOP + TAU * c.p;
    sector(c.ground, cx, cy, R, TOP, TAU * c.p, INK, F.sector_alpha);
    line(c, cx + Math.cos(a1) * F.hand_r_px, cy + Math.sin(a1) * F.hand_r_px,
      cx + Math.cos(a1) * R, cy + Math.sin(a1) * R, F.hand_width_px, FLASH, F.hand_alpha);
    ring(c, c.rings, cx, cy, R, F.rim_width_px, INK, F.rim_alpha);
    // The card strokes a dark rim round the digit, so it takes the halo.
    c.text(clockDigits(c.leftSec), cx, cy + F.text_dy_px, F.text_px, FLASH, F.text_alpha, 'centre', true);
    return;
  }
  const k = flash(c.readyAgo, F.flash_sec);
  disc(c.ground, cx, cy, R, INK, F.ready_fill_alpha + F.fill_flash_alpha * k);
  ring(c, c.rings, cx, cy, R + F.ready_rim_px * k, F.ready_rim_width_px, FLASH,
    F.ready_rim_alpha + F.rim_flash_alpha * k);
  line(c, cx + F.tick_x0_px, cy + F.tick_y0_px, cx + F.tick_x1_px, cy + F.tick_y1_px,
    F.tick_width_px, FLASH, F.tick_alpha);
  line(c, cx + F.tick_x1_px, cy + F.tick_y1_px, cx + F.tick_x2_px, cy + F.tick_y2_px,
    F.tick_width_px, FLASH, F.tick_alpha);
}

// ---------------------------------------------------------------------------
// Card 04, COUNTDOWN TAG
// ---------------------------------------------------------------------------

/**
 * A small mono tag under the hull on a dark plate. While the tube reloads it
 * reads `RLD` and the whole seconds left, white just after the shot. At
 * ready it reads `READY` in white for `flash_sec` and then a dim `READY`.
 * Empty tubes read `EMPTY` in the faint ink.
 */
function countdownTag(c: ReloadCtx): void {
  const F = figs(c, 'countdown-tag');
  let s: string, ink: number, a: number, plate: number;
  if (c.empty) {
    s = S.reload_tag_empty; ink = FAINT; a = F.empty_alpha; plate = F.plate_alpha;
  } else if (c.reloading) {
    s = tagReloading(c.leftSec);
    ink = c.firedAgo >= 0 && c.firedAgo < F.fired_sec ? FLASH : INK;
    a = F.reload_alpha; plate = F.plate_alpha;
  } else {
    const lit = c.readyAgo >= 0 && c.readyAgo < F.flash_sec;
    s = S.reload_tag_ready; ink = lit ? FLASH : INK;
    a = lit ? F.ready_alpha : F.rest_alpha;
    plate = lit ? F.ready_plate_alpha : F.rest_plate_alpha;
  }
  const y = c.y + F.y_px;
  const w = c.measure(s, F.text_px) + F.pad_px;
  rect(c, c.x - w / 2, y - F.plate_px / 2, w, F.plate_px, DEEP, plate);
  c.text(s, c.x, y + F.text_dy_px, F.text_px, ink, a, 'centre', false);
}

// ---------------------------------------------------------------------------
// Card 05, ROUND LABEL
// ---------------------------------------------------------------------------

/**
 * A label on the right of the hull names the rounds aboard and their kind in
 * words. While the tube reloads the label dims and carries the seconds; at
 * ready it drops the seconds, turns bright and flashes white once. Empty
 * tubes read `0` and the kind in the faint ink, with a line through.
 */
function roundLabel(c: ReloadCtx): void {
  const F = figs(c, 'round-label');
  const x0 = c.x + F.x_px, y0 = c.y + F.y_px;
  if (c.empty) {
    const s = label(0, c.name, -1);
    const w = c.text(s, x0, y0, F.text_px, FAINT, F.empty_alpha, 'left', false);
    line(c, x0 - F.strike_px, y0, x0 + w + F.strike_px, y0, F.strike_width_px, FAINT, F.empty_alpha);
  } else if (c.reloading) {
    c.text(label(c.count, c.name, c.leftSec), x0, y0, F.text_px, INK, F.reload_alpha, 'left', false);
  } else {
    const k = flash(c.readyAgo, F.flash_sec);
    c.text(label(c.count, c.name, -1), x0, y0, F.text_px, k > 0 ? FLASH : INK, F.ready_alpha, 'left', false);
  }
}

// ---------------------------------------------------------------------------
// Card 03, TUBE GAUGE
// ---------------------------------------------------------------------------

/**
 * A short upright gauge right of the hull, screen-upright. The small top
 * cell is the tube: it fills from the bottom while the tube reloads and
 * turns white at ready. The long cell below is the magazine: it fills to
 * the rounds aboard over the magazine, with a divider per round, or per
 * `step_count` rounds for a large magazine. The letter at the foot names
 * the kind. Empty tubes show a hollow gauge with a slash through the tube
 * cell.
 */
function tubeGauge(c: ReloadCtx): void {
  const F = figs(c, 'tube-gauge');
  const x0 = c.x + F.x_px, w = F.gauge_px;
  const ty = c.y + F.tube_y_px, th = F.tube_px, my = c.y + F.mag_y_px, mh = F.mag_px;
  const k = c.ready ? flash(c.readyAgo, F.flash_sec) : 0;
  const rim = c.empty ? FAINT : INK;
  rect(c, x0, ty, w, th, GROUND, F.ground_alpha);
  rect(c, x0, my, w, mh, GROUND, F.ground_alpha);
  if (!c.empty) {
    const f = c.reloading ? c.p : 1;
    rect(c, x0, ty + th * (1 - f), w, th * f, c.ready ? FLASH : INK,
      c.ready ? F.ready_alpha + F.ready_flash_alpha * k : F.tube_alpha);
    const mf = c.mag > 0 ? c.count / c.mag : 0;
    rect(c, x0, my + mh * (1 - mf), w, mh * mf, INK, F.mag_alpha);
  }
  const ra = c.empty ? F.empty_rim_alpha : F.rim_alpha;
  rectStroke(c, x0, ty, w, th, F.rim_width_px, rim, ra);
  rectStroke(c, x0, my, w, mh, F.rim_width_px, rim, ra);
  const step = c.mag > F.split_count ? F.step_count : 1;
  for (let i = step; i < c.mag; i += step) {
    const yy = my + (mh * i) / c.mag;
    line(c, x0, yy, x0 + w, yy, F.divider_width_px, DEEP, F.divider_alpha);
  }
  if (c.empty) line(c, x0, ty + th, x0 + w, ty, F.slash_width_px, FAINT, F.slash_alpha);
  if (k > 0) {
    rectStroke(c, x0 - F.frame_px, ty - F.frame_px, w + 2 * F.frame_px, th + 2 * F.frame_px,
      F.frame_width_px, FLASH, k);
  }
  c.text(c.letter, x0 + w / 2, my + mh + F.letter_px, F.text_px, rim,
    c.empty ? F.empty_letter_alpha : F.letter_alpha, 'centre', false);
}

// ---------------------------------------------------------------------------
// Card 01, ARC BAR
// ---------------------------------------------------------------------------

/**
 * A thin arc round the hull that fills clockwise over the reload, with a
 * white bead at its head. It leaves a gap at the top for the readout bars.
 * Outside it, one short tick per round of the magazine: a lit tick is a
 * round aboard, a dim tick an empty place. At ready the full arc flashes
 * white and then stays at a low alpha. Empty tubes show a dashed arc and
 * every tick dim.
 */
function arcBar(c: ReloadCtx): void {
  const F = figs(c, 'arc-bar');
  const a0 = TOP + F.gap_rad, span = TAU - 2 * F.gap_rad, R = F.r_px;
  const s = c.rings;
  if (c.empty) {
    dashedArc(c, s, c.x, c.y, R, F.empty_width_px, a0, span, F.empty_dash_px, F.empty_gap_px,
      FAINT, F.empty_alpha);
  } else {
    arc(c, s, c.x, c.y, R, F.width_px, a0, span, INK, F.track_alpha);
    if (c.reloading) {
      const a1 = a0 + span * c.p;
      arc(c, s, c.x, c.y, R, F.width_px, a0, span * c.p, INK, F.progress_alpha);
      disc(s, c.x + Math.cos(a1) * R, c.y + Math.sin(a1) * R, F.bead_r_px, FLASH, F.bead_alpha);
    } else {
      const k = flash(c.readyAgo, F.flash_sec);
      arc(c, s, c.x, c.y, R, F.width_px + F.flash_width_px * k, a0, span,
        k > 0 ? FLASH : INK, F.rest_alpha + F.flash_alpha * k);
    }
  }
  for (let i = 0; i < c.mag; i++) {
    const a = a0 + (span * (i + 0.5)) / c.mag, on = i < c.count;
    const ca = Math.cos(a), sa = Math.sin(a);
    line(c, c.x + ca * F.tick_r_px, c.y + sa * F.tick_r_px, c.x + ca * F.tick_r2_px, c.y + sa * F.tick_r2_px,
      F.tick_width_px, on ? INK : FAINT, on ? F.tick_alpha : F.tick_dim_alpha);
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, ReloadRecipe>> = {
  'pip-row': pipRow,
  'sweep-segments': sweepSegments,
  'loading-slide': loadingSlide,
  'clock-digit': clockDigit,
  'countdown-tag': countdownTag,
  'round-label': roundLabel,
  'tube-gauge': tubeGauge,
  'arc-bar': arcBar,
};

/** The looks still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
